import { useEffect, useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Zap } from "lucide-react";
import { readStreak, buildRecentActivity } from "@/lib/streak";

/**
 * Deep Work Streak pill for the header.
 *
 * Behavior:
 *  - On mount, ticks today's visit (idempotent per day).
 *  - Renders a compact pill: <Zap /> {N}d
 *  - Tap opens a popover with:
 *      • Big current number + "day" / "days"
 *      • 14-day dot strip (filled = you showed up)
 *      • Personal best line
 *      • Gentle no-shame copy
 *  - When streak === 0 (never visited before render — extremely rare because
 *    we tick on mount), we simply hide the pill.
 */
export default function StreakPill() {
  const [state, setState] = useState(() => readStreak());

  // Re-read localStorage on window focus / storage events so an engagement tick
  // triggered elsewhere (Feed.jsx marks a card read → calls tickStreak) is
  // reflected immediately without a full reload. NB: no automatic tick on mount
  // anymore — a fresh visitor stays at 0 until they actually engage with content,
  // which prevents the "phantom 1-day streak in incognito" bug.
  useEffect(() => {
    const sync = () => setState(readStreak());
    window.addEventListener("focus", sync);
    window.addEventListener("storage", sync);
    // Poll gently for same-tab writes (storage event only fires cross-tab).
    const id = setInterval(sync, 4_000);
    return () => {
      window.removeEventListener("focus", sync);
      window.removeEventListener("storage", sync);
      clearInterval(id);
    };
  }, []);

  const recent = useMemo(() => buildRecentActivity(14), [state.last_visit_date, state.current]);

  if (!state.current || state.current < 1) return null;

  const dayWord = state.current === 1 ? "day" : "days";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Deep work streak: ${state.current} ${dayWord}. Tap for details.`}
          data-testid="header-streak-pill"
          className="group inline-flex items-center gap-1.5 rounded-full border border-[#26334D] bg-[#1A2232] px-2.5 py-1.5 text-[11px] font-semibold text-[#E85A2A] hover:bg-[#232E45] active:scale-[0.98] transition-colors shadow-[0_2px_8px_rgba(232,90,42,0.15)]"
        >
          <Zap
            className="h-3.5 w-3.5 text-[#E85A2A]"
            aria-hidden
            fill="currentColor"
            strokeWidth={0}
          />
          <span className="tabular-nums" data-testid="header-streak-count">
            {state.current}d
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={10}
        data-testid="header-streak-popover"
        className="w-[300px] p-0 rounded-2xl border border-[#26334D] bg-[#1A2232] shadow-[0_20px_50px_rgba(0,0,0,0.45)]"
      >
        <div className="p-4 pb-3 border-b border-[#26334D]">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-widest text-[#94A3B8]">
                Deep Work Streak
              </div>
              <div className="mt-0.5 flex items-baseline gap-1.5">
                <span
                  className="text-3xl font-semibold tabular-nums text-[#E85A2A] leading-none"
                  data-testid="streak-current-big"
                >
                  {state.current}
                </span>
                <span className="text-sm text-[#94A3B8]">{dayWord} on</span>
              </div>
            </div>
            <div
              className="inline-flex items-center gap-1 rounded-full bg-[#F2BD68]/10 border border-[#F2BD68]/30 px-2 py-0.5 text-[10.5px] font-semibold text-[#F2BD68]"
              data-testid="streak-best-badge"
              title="Your personal best streak"
            >
              <Zap className="h-3 w-3" aria-hidden fill="currentColor" strokeWidth={0} />
              Best {state.best || state.current}d
            </div>
          </div>
        </div>

        <div className="p-4 pt-3">
          <div className="text-[10.5px] font-semibold uppercase tracking-widest text-[#94A3B8]">
            Last 14 days
          </div>
          <div
            className="mt-2 grid grid-cols-14 gap-1"
            style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }}
            role="img"
            aria-label={`Activity in the last 14 days: ${recent.filter(d => d.active).length} of 14 days.`}
            data-testid="streak-history-strip"
          >
            {recent.map((d, i) => (
              <span
                key={d.date}
                title={d.date + (d.active ? " · showed up" : " · missed")}
                data-testid={d.active ? "streak-dot-active" : "streak-dot-inactive"}
                className={`h-5 rounded-md border ${
                  d.active
                    ? "bg-[#E85A2A] border-[#E85A2A]"
                    : "bg-[#1A2232] border-[#26334D]"
                }`}
                aria-hidden={i > 0}
              />
            ))}
          </div>

          <p className="mt-3 text-[11.5px] text-[#94A3B8]" style={{ lineHeight: 1.55 }}>
            No pressure. Show up on the days you can. Miss one and it quietly resets —
            your <span className="font-semibold text-[#F2BD68]">Best</span> stays with you.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
